// Render-props component that store updated responses for CA form parts in local state
// and provides function to update them.
// It requires two parameters:
// * id: unique id of form part.
// * response: object representing value of the form part. Might be any shape depending on the
//   form part type.
// Calls child function with the object with following fields:
// * newResponse: original response if it is not changed or updated response stored in local
//   state.
// * handleChangeResponse: function that could be used to store updated response. The parameter
//   of this function should be the same structure than the original response.
// Example:
// <ChangedResponse id="formId" response={{ answer: 'Original Value' }}>
//   {({ newResponse, handleChangeResponse }) => (
//     <input
//       value={newResponse.answer}
//       onChange={e => handleChangeResponse({ answer: e.target.value })}
//     />
//   )}
// </ChangedResponse>
// Real world example: static/bundles/compound-assessments/components/tunnelvision/FormPart.jsx
// It will save updated responses in local state
// You can get updated response anywhere with `ChangedResponse` component.
import * as React from 'react';

import { isEqual } from 'lodash';

import type { UpdateChangedResponse } from 'bundles/compound-assessments/components/local-state/changed-response/ChangedResponseContext';
import { ChangedResponseContext } from 'bundles/compound-assessments/components/local-state/changed-response/ChangedResponseContext';

import type { Response } from './types';
import formChangedResponseId from './utils/formChangedResponseId';

type RenderProps = {
  newResponse: Response;
  handleChangeResponse: (x0: Response) => void;
};

type InputProps = {
  id: string;
  localScopeId?: string | null;
  response: Response;
  children: (x0: RenderProps) => JSX.Element;
  // Quizzes depends on local state being reset when the form part is re-mounted, to support multiple attempts. SGA
  // Quick Grader wants the opposite behavior. Previously, this was handled at the async loading state whims of
  // apollo@2, but now we must explicitly request the desired behavior as the new React context implementation of the
  // local responses is synchronous, and has no loading state baggage.
  shouldResetLocalResponseOnMount?: boolean;
};

type Props = InputProps & {
  localResponse: Response | null;
  updateChangedResponse: UpdateChangedResponse;
};

class InnerChangedResponse extends React.Component<Props> {
  componentDidMount() {
    const { shouldResetLocalResponseOnMount, localResponse } = this.props;
    if (shouldResetLocalResponseOnMount || !localResponse) {
      // Initialize localResponse when the component is first loaded. Re-initialized when asked to (the default),
      // treating each mounting of the component as a new "attempt" from scratch.
      this.handleChangeResponse(this.props.response);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { response, localResponse } = this.props;
    if (!isEqual(prevProps.response, response) && !localResponse) {
      // Initialize localResponse if response changes, but don't overwrite.
      this.handleChangeResponse(response);
    }
  }

  handleChangeResponse = (changedResponse: Response) => {
    const { id } = this.props;

    // We need data to be changed synchronously to avoid jumping cursor in form fields.
    // This function maintains a stable reference but updates the state synchronously.
    return this.props.updateChangedResponse(id, changedResponse);
  };

  render() {
    const { response, children, localResponse } = this.props;

    const newResponse = localResponse || response;

    return children({ newResponse, handleChangeResponse: this.handleChangeResponse });
  }
}

const ChangedResponse = ({
  id: responseId,
  localScopeId,
  response,
  children,
  shouldResetLocalResponseOnMount = true,
}: InputProps) => {
  const id = formChangedResponseId(responseId, localScopeId);
  const { getChangedResponse, updateChangedResponse } = React.useContext(ChangedResponseContext);
  const localResponse = getChangedResponse(id)?.response;

  return (
    <InnerChangedResponse
      response={response}
      localResponse={localResponse}
      id={id}
      updateChangedResponse={updateChangedResponse}
      shouldResetLocalResponseOnMount={shouldResetLocalResponseOnMount}
    >
      {children}
    </InnerChangedResponse>
  );
};

export default ChangedResponse;
